/**
 * Cliente HTTP de la galería de screenshots — Fase 10d.
 *
 * GET es público — el frontend lo llama sin cookies opcionalmente.
 * POST/DELETE requieren sesión; el caller decide cuándo invocarlos
 * según el rol del visitante.
 */

import type { GalleryListResponse, GalleryPhoto } from '@51esc/shared';

export type FetchGalleryResult =
  | { ok: true; data: GalleryListResponse }
  | { ok: false; message: string };

export type UploadResult =
  | { ok: true; photos: GalleryPhoto[] }
  | { ok: false; message: string };

export type DeleteResult =
  | { ok: true }
  | { ok: false; message: string };

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    /* swallow */
  }
  return `El servidor respondió con ${String(res.status)}.`;
}

export async function fetchGallery(): Promise<FetchGalleryResult> {
  let res: Response;
  try {
    res = await fetch('/api/screenshots', { credentials: 'include' });
  } catch (err) {
    console.error('[galeria] fetch network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | GalleryListResponse
    | null;
  if (!body || !Array.isArray(body.photos)) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body };
}

/**
 * Sube uno o varios archivos. El backend acepta hasta 5 en un mismo
 * request; el caller debe respetar el límite o recibirá 400. La
 * `caption` aplica a TODAS las fotos del request.
 */
export async function uploadGalleryPhotos(
  files: File[],
  caption: string | null,
): Promise<UploadResult> {
  const fd = new FormData();
  if (caption) fd.append('caption', caption);
  for (const f of files) fd.append('photos', f);

  let res: Response;
  try {
    res = await fetch('/api/screenshots', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
  } catch (err) {
    console.error('[galeria] upload network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { photo: GalleryPhoto }
    | { photos: GalleryPhoto[] }
    | null;
  if (!body) return { ok: false, message: 'Respuesta del servidor inválida.' };
  if ('photo' in body) return { ok: true, photos: [body.photo] };
  if (Array.isArray(body.photos)) return { ok: true, photos: body.photos };
  return { ok: false, message: 'Respuesta del servidor inválida.' };
}

export async function deleteGalleryPhoto(id: string): Promise<DeleteResult> {
  let res: Response;
  try {
    res = await fetch(`/api/screenshots/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[galeria] delete network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok && res.status !== 204) {
    return { ok: false, message: await readError(res) };
  }
  return { ok: true };
}
